import styled from '@emotion/styled'
import { LandingPageModel } from '../../../models/landingPageModel'
import Image from 'next/image'
import { HeaderLogoSize } from '@storefronts/common-types'
import { useContext } from 'react'
import { AnalyticsContext } from '../../context/analyticsContext'
import { PageContext } from '../../context/PageContextProvider'
import { imageUrlTransform } from '../../utils/imageUrlTransform'
import { CloseIcon } from '../icons/Close'
import { VerifiedBadgeIcon } from '../icons/Icons'
import { CartIcon } from './CartIcon'
import {
    CustomizationContext,
    HEADER_BORDER_COLOR_VAR,
    PRIMARY_ICON_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../style/typography'
import { scaledFontSize } from '../../style/scrollingLayout'

type HeaderProps = {
    landingPage: LandingPageModel
    hideCartIcon?: boolean
    showHeader?: boolean
    showCloseButton?: boolean
}

/**
 * MEDIUM keeps the original sizing, which shows a logo at its own size up to
 * the caps. The other sizes set the height, so a small image scales up to it.
 */
const LOGO_DIMENSIONS_PX: Record<
    HeaderLogoSize,
    {
        maxWidth: number
        maxHeight: number
        height: number | 'auto'
        avatar: number
    }
> = {
    SMALL: { maxWidth: 160, maxHeight: 28, height: 28, avatar: 36 },
    MEDIUM: { maxWidth: 180, maxHeight: 35, height: 'auto', avatar: 43 },
    LARGE: { maxWidth: 240, maxHeight: 48, height: 48, avatar: 56 },
}

const HeaderContainer = styled.div<{
    showHeader: boolean
    centeredLogo: boolean
}>(
    {
        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        alignItems: 'center',
        padding: '10px 15px 10px 20px',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
        transform: 'translate3d(0px, 0px, 0px)',
    },
    ({ centeredLogo }) =>
        centeredLogo
            ? {
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  columnGap: 12,
              }
            : {
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
              },
    ({ showHeader }) => ({
        borderBottom: showHeader
            ? `1px solid var(${HEADER_BORDER_COLOR_VAR})`
            : 'none',
    }),
)

const ImgContainer = styled.div({
    display: 'flex',
})

const HeaderTextContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    textAlign: 'left',
    paddingLeft: 10,
    lineHeight: 1.6,
})

const FlexContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
})

const HeaderActions = styled.div<{ centeredLogo: boolean }>(
    {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    ({ centeredLogo }) =>
        centeredLogo
            ? {
                  gridColumn: 3,
                  justifySelf: 'end',
              }
            : {},
)

const LogoButton = styled.button<{ centered: boolean }>(({ centered }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: centered ? 'center' : 'flex-start',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    gridColumn: centered ? 2 : undefined,
}))

const TitleText = styled.span({
    ...getTypographyRoleStyle('navText', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 500,
        lineHeight: 1.3,
    }),
    fontSize: scaledFontSize(14),
})

const SubtitleText = styled.span({
    ...getTypographyRoleStyle('captionText', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1.3,
    }),
    fontSize: scaledFontSize(12),
})

export const AllProductsHeader: React.FC<HeaderProps> = ({
    landingPage,
    hideCartIcon = false,
    showHeader = true,
    showCloseButton = false,
}) => {
    const {
        isCartOpen,
        setIsCartOpen,
        activeLPLink,
        setActiveLPLink,
        isGridExpanded,
        setIsGridExpanded,
    } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const {
        headerLogoSize = 'MEDIUM',
        headerLogoAlignment = 'CENTER',
        headerVerifiedBadgeEnabled = true,
    } = useContext(CustomizationContext)
    const logo = LOGO_DIMENSIONS_PX[headerLogoSize]

    const logoImageUrl = landingPage.client.logoImageUrl
    const logoUrl = logoImageUrl ? imageUrlTransform(logoImageUrl) : null
    const hasLogo = showHeader && logoUrl !== null
    const hasCenteredLogo = hasLogo && headerLogoAlignment === 'CENTER'
    const brandName =
        landingPage.client.displayName || landingPage.title || 'Brand'

    const onIconClick = () => {
        if (isCartOpen || activeLPLink || isGridExpanded) {
            setIsCartOpen(false)
            setActiveLPLink(null)
            setIsGridExpanded(false)
            Analytics.TRACK_HEADER_LOGO_CLICK({
                type: 'page_action',
            })
        }
        const clientMainDomain = landingPage.client.mainDomain
        if (clientMainDomain) {
            Analytics.TRACK_HEADER_LOGO_CLICK({
                type: 'homepage_redirect',
            })
            setTimeout(() => {
                window.location.href = `https://${clientMainDomain}`
            }, 500)
        }
    }

    const onCloseClick = () => {
        setIsCartOpen(false)
        setActiveLPLink(null)
        setIsGridExpanded(false)
    }

    return (
        <HeaderContainer showHeader={showHeader} centeredLogo={hasCenteredLogo}>
            {hasLogo ? (
                <>
                    {hasCenteredLogo && <div aria-hidden="true" />}
                    <LogoButton
                        type="button"
                        onClick={onIconClick}
                        aria-label={brandName}
                        centered={hasCenteredLogo}
                    >
                        <Image
                            src={logoUrl}
                            alt=""
                            aria-hidden={true}
                            width={logo.maxWidth}
                            height={logo.maxHeight}
                            priority={true}
                            style={{
                                width: 'auto',
                                height: logo.height,
                                maxWidth: logo.maxWidth,
                                maxHeight: logo.maxHeight,
                                objectFit: 'contain',
                            }}
                        />
                    </LogoButton>
                </>
            ) : (
                <FlexContainer onClick={onIconClick}>
                    {showHeader && (
                        <>
                            <ImgContainer>
                                <Image
                                    style={{
                                        borderRadius: '50%',
                                        cursor: 'pointer',
                                    }}
                                    width={logo.avatar}
                                    height={logo.avatar}
                                    src={imageUrlTransform(
                                        landingPage.client.imageUrl ||
                                            landingPage.imageUrl,
                                    )}
                                    priority={true}
                                    alt=""
                                />
                            </ImgContainer>
                            <HeaderTextContainer>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <TitleText>{brandName}</TitleText>
                                    {headerVerifiedBadgeEnabled && (
                                        <VerifiedBadgeIcon />
                                    )}
                                </div>
                                <SubtitleText>{landingPage.title}</SubtitleText>
                            </HeaderTextContainer>
                        </>
                    )}
                </FlexContainer>
            )}
            <HeaderActions centeredLogo={hasCenteredLogo}>
                {!hideCartIcon && showHeader && <CartIcon />}
                {showCloseButton && (
                    <div
                        onClick={onCloseClick}
                        style={{
                            backgroundColor: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                        }}
                    >
                        <CloseIcon
                            width={32}
                            height={32}
                            fill={`var(${PRIMARY_ICON_COLOR_VAR})`}
                        />
                    </div>
                )}
            </HeaderActions>
        </HeaderContainer>
    )
}
